/* @flow */

import type { STREAM_PRIORITIES_BY_TYPE_TYPE, STREAM_PRIORITIES_TYPE } from '../../../helpers/ui/metadata/Types';
import type { BO_API_PARAMETERS_TYPE } from '../../../helpers/videofutur/queryParameters';
import type { NETGEM_API_DMS_ASSOCIATED_DEVICE } from '../../../libs/netgemLibrary/dms/types/AssociatedDevice';
import type { DmsSettingMap } from '../../../libs/netgemLibrary/dms/types/DeviceInfoSettings';
import type { NETGEM_API_DMS_QUOTA_PVR } from '../../../libs/netgemLibrary/dms/types/QuotaPvr';
import type { ChannelMap } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_DATA_COLLECTION_BATCH_PAYLOAD } from '../../../libs/netgemLibrary/v8/types/DataCollection';
import type { NETGEM_API_V8_REQUEST_HEADERS } from '../../../libs/netgemLibrary/v8/types/Headers';
import type { ScheduledRecordingsKindType } from '../../../libs/netgemLibrary/v8/types/Npvr';
import type { NETGEM_API_PARENTAL_CONTROL } from '../../../libs/netgemLibrary/v8/types/ParentalControl';
import type { NETGEM_API_V8_SECTION } from '../../../libs/netgemLibrary/v8/types/Section';
import type { CombinedReducers } from '../../reducers';
import type { Dispatch, Store } from '../../types/types';

import Collector from '../../../helpers/dataCollection/collector';
import { getDataCollectionSettings } from '../../../helpers/dataCollection/settings';
import { logError } from '../../../helpers/debug/debug';
import GameManager from '../../../helpers/gaming/gameManager';
import { parseBoolean, parseInteger } from '../../../helpers/jsHelpers/parser';
import { fireAndForget } from '../../../helpers/jsHelpers/promise';
import { type KeyValuePair, isUndefinedOrNull } from '../../../helpers/jsHelpers/types';
import { StorageKeys } from '../../../helpers/localStorage/keys';
import LocalStorageManager from '../../../helpers/localStorage/localStorageManager';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import ProfilesManager from '../../../helpers/profiles/profilesManager';
import NetgemApi from '../../../libs/netgemLibrary';
import { createCustomNetworkErrorFromKey } from '../../../libs/netgemLibrary/helpers/CreateCustomNetworkError';
import { CustomNetworkError } from '../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { HttpMethod } from '../../../libs/netgemLibrary/v8/types/HttpMethod';
import {
  updateBOProductId,
  updateBOSettings,
  updateBOState,
  updateDeviceChannels,
  updateDeviceSettings,
  updateDidomiConfiguration,
  updateDistributorAppKeys,
  updateHideVodPreviewDates,
  updateMainDistributor,
  updateMeshState,
  updatePlayerStreamPriorities,
} from '../../appConf/actions';
import { unregisterApplication, updateAssociatedDevices, updateAuthenticationToken } from '../../appRegistration/actions';
import { updateNpvrState, updateQuotaPvr } from '../../npvr/actions';
import {
  REDUX_MSG_REQUEST_BO_AUTHENTICATE,
  REDUX_MSG_REQUEST_BO_DISCOVERY,
  REDUX_MSG_REQUEST_BO_FAVORITE_ADD,
  REDUX_MSG_REQUEST_BO_FAVORITE_DELETE,
  REDUX_MSG_REQUEST_BO_FAVORITE_LIST,
  REDUX_MSG_REQUEST_BO_PERSONAL_DATA,
  REDUX_MSG_REQUEST_BO_PURCHASE,
  REDUX_MSG_REQUEST_BO_PURCHASE_LIST,
  REDUX_MSG_REQUEST_BO_PURCHASE_STATUS,
  REDUX_MSG_REQUEST_BO_STREAMS_GET,
  REDUX_MSG_REQUEST_BO_STREAM_CREATE,
  REDUX_MSG_REQUEST_BO_STREAM_START,
  REDUX_MSG_REQUEST_BO_STREAM_STOP,
  REDUX_MSG_REQUEST_CUSTOM_STRATEGY,
  REDUX_MSG_REQUEST_DATA_COLLECTION,
  REDUX_MSG_REQUEST_DATA_COLLECTION_COLLECTOR_ID,
  REDUX_MSG_REQUEST_ENTITLEMENT_GET,
  REDUX_MSG_REQUEST_ENTITLEMENT_RELEASE,
  REDUX_MSG_REQUEST_GENERIC,
  REDUX_MSG_REQUEST_GET_ASSOCIATED_DEVICES,
  REDUX_MSG_REQUEST_GET_DEVICE_CHANNELS,
  REDUX_MSG_REQUEST_GET_DEVICE_QUOTA_PVR,
  REDUX_MSG_REQUEST_GET_DEVICE_SETTINGS,
  REDUX_MSG_REQUEST_LIST_ALIAS_MULTI,
  REDUX_MSG_REQUEST_LIST_ALIAS_POST,
  REDUX_MSG_REQUEST_LOGIN,
  REDUX_MSG_REQUEST_LOGOUT,
  REDUX_MSG_REQUEST_NPVR_RECORDINGS_DELETE,
  REDUX_MSG_REQUEST_NPVR_RECORDINGS_FUTURE,
  REDUX_MSG_REQUEST_NPVR_RECORDINGS_LIST,
  REDUX_MSG_REQUEST_NPVR_RECORDINGS_METADATA,
  REDUX_MSG_REQUEST_NPVR_RECORDINGS_QUOTA,
  REDUX_MSG_REQUEST_NPVR_RECORDINGS_RETRY,
  REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_CREATE,
  REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_DELETE,
  REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_LIST,
  REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_LIST_FULL,
  REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_METADATA,
  REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_UPDATE,
  REDUX_MSG_REQUEST_PERSONAL_DATA_DELETE,
  REDUX_MSG_REQUEST_PERSONAL_DATA_GET,
  REDUX_MSG_REQUEST_PERSONAL_DATA_POST,
  REDUX_MSG_REQUEST_SECTION_FEED,
} from '../constants/';
import { getSettingValueByNameFromDeviceSettings } from './helpers/settings';

let ntgApiInstance: NetgemApi | null = null;

const getDefaultRestrictions = (deviceSettings: DmsSettingMap, section: string): NETGEM_API_PARENTAL_CONTROL | null => {
  const defaultMaxAgeAllowedForVideo = getSettingValueByNameFromDeviceSettings(deviceSettings, section, 'maxAgeAllowedForVideo');
  const defaultMaxAgeAllowedForGames = getSettingValueByNameFromDeviceSettings(deviceSettings, section, 'maxAgeAllowedForGames');
  const defaultLockPurchase = getSettingValueByNameFromDeviceSettings(deviceSettings, section, 'lockPurchase');
  const defaultLockedChannelIds = getSettingValueByNameFromDeviceSettings(deviceSettings, section, 'lockedChannelIds');

  if (!defaultMaxAgeAllowedForVideo && !defaultMaxAgeAllowedForGames && !defaultLockPurchase && !defaultLockedChannelIds) {
    return null;
  }

  const defaultRestrictions: NETGEM_API_PARENTAL_CONTROL = {};

  if (defaultMaxAgeAllowedForVideo) {
    defaultRestrictions.maxAgeAllowedForVideo = parseInteger(defaultMaxAgeAllowedForVideo);
  }

  if (defaultMaxAgeAllowedForGames) {
    defaultRestrictions.maxAgeAllowedForGames = parseInteger(defaultMaxAgeAllowedForGames);
  }

  if (defaultLockPurchase) {
    defaultRestrictions.lockPurchase = parseBoolean(defaultLockPurchase);
  }

  if (defaultLockedChannelIds) {
    defaultRestrictions.lockedChannelIds = defaultLockedChannelIds.split(',');
  }

  return defaultRestrictions;
};

const buildPriorities: (streamPriorities: ?string) => STREAM_PRIORITIES_TYPE | null = (streamPriorities) => {
  if (!streamPriorities) {
    return null;
  }

  const priorities: STREAM_PRIORITIES_TYPE = { default: {} };

  const prioritiesRaw = streamPriorities.split(',') ?? [];
  prioritiesRaw.forEach((p) => {
    const [name, ...value] = p.split('.');

    if (value.length === 0) {
      priorities.default[name] = Object.keys(priorities.default).length;
    } else {
      let pList: STREAM_PRIORITIES_BY_TYPE_TYPE = priorities[name];
      if (!pList) {
        pList = {} as STREAM_PRIORITIES_BY_TYPE_TYPE;
        priorities[name] = pList;
      }

      pList[value.join('.')] = Object.keys(pList).length;
    }
  });

  return priorities;
};

const extractDistributorAppKeys: (settings: DmsSettingMap) => KeyValuePair<string> = (settings) => {
  const appKeys: KeyValuePair<string> = {};

  const { 'platform.vf.appkey': list } = settings;

  if (list) {
    list.forEach(({ name, value }) => {
      appKeys[name] = value;
    });
  }

  return appKeys;
};

const createNetgemApiLibraryInstance: (dispatch: Dispatch, getState: () => CombinedReducers) => void = (dispatch, getState) => {
  const connectedCallback = (authenticationToken: string | null) => {
    const {
      appConfiguration: { configuration },
    } = getState();

    dispatch(updateAuthenticationToken(authenticationToken, configuration));
  };

  const disconnectedCallback = () => {
    dispatch(unregisterApplication());
    dispatch(updateAuthenticationToken(null, {}));
  };

  const deviceChannelsCallback: (deviceChannels: ChannelMap) => void = (deviceChannels) => dispatch(updateDeviceChannels(deviceChannels));

  const associatedDevicesCallback = (associatedDevices: Array<NETGEM_API_DMS_ASSOCIATED_DEVICE>) => dispatch(updateAssociatedDevices(associatedDevices));

  const quotaPvrCallback = (quotaPvr: ?NETGEM_API_DMS_QUOTA_PVR) => dispatch(updateQuotaPvr(quotaPvr));

  const boAuthenticationCallback = (settings?: KeyValuePair<string>) => {
    dispatch(updateBOState(!isUndefinedOrNull(settings)));
    dispatch(updateBOSettings(settings ?? {}));
  };

  const deviceSettingsCallback: (deviceSettings: DmsSettingMap) => void = (deviceSettings) => {
    dispatch(updateDeviceSettings(deviceSettings));
    dispatch(updateDistributorAppKeys(extractDistributorAppKeys(deviceSettings)));

    // Initialize main distributor Id
    const distributorId = getSettingValueByNameFromDeviceSettings(deviceSettings, 'platform.vf', 'distributor');
    if (distributorId) {
      dispatch(updateMainDistributor(distributorId));
    }

    // Store value for defaultProductId
    const defaultProductId = getSettingValueByNameFromDeviceSettings(deviceSettings, 'platform.vf', 'defaultProductId');
    if (defaultProductId) {
      dispatch(updateBOProductId(defaultProductId));
    }

    // Initialize stream priorities (DASH, HLS, etc.)
    const streamPriorities = getSettingValueByNameFromDeviceSettings(deviceSettings, 'videoplayer', 'streampriorities');
    dispatch(updatePlayerStreamPriorities(buildPriorities(streamPriorities)));

    // Initialize NPVR state
    const isEnabled = parseBoolean(getSettingValueByNameFromDeviceSettings(deviceSettings, 'npvr', 'enabled'));
    const recFromBeginning = parseBoolean(getSettingValueByNameFromDeviceSettings(deviceSettings, 'npvr', 'rec_from_beginning'));
    const keepFromReplay = parseBoolean(getSettingValueByNameFromDeviceSettings(deviceSettings, 'npvr.keep_from_replay', 'enabled'));
    const startMargin = parseInteger(getSettingValueByNameFromDeviceSettings(deviceSettings, 'npvr.margin', 'start'));
    const endMargin = parseInteger(getSettingValueByNameFromDeviceSettings(deviceSettings, 'npvr.margin', 'end'));
    dispatch(updateNpvrState(isEnabled, recFromBeginning, keepFromReplay, startMargin, endMargin));

    // Initialize mesh state
    const meshSetting = getSettingValueByNameFromDeviceSettings(deviceSettings, 'mesh', 'enabled');
    const isMeshEnabled = meshSetting !== null ? parseBoolean(meshSetting) : null;
    dispatch(updateMeshState(isMeshEnabled));

    const realm = getSettingValueByNameFromDeviceSettings(deviceSettings, 'platform', 'realm') || 'unknown realm';

    // Initialize data collector
    const {
      appConfiguration: { versionApp },
    } = getState();
    const dataCollectionSettings = getDataCollectionSettings(deviceSettings);
    Collector.initialize(dispatch, versionApp, realm, dataCollectionSettings);

    // Initialize parental control manager by passing it the base URL and an authentication token
    const {
      appRegistration: { authenticationToken },
    } = getState();
    const parentalControlBaseUrl = getSettingValueByNameFromDeviceSettings(deviceSettings, 'parentalcontrol.pincode', 'baseurl');
    const countryCode = getSettingValueByNameFromDeviceSettings(deviceSettings, 'parentalControl', 'countryCodeForPreferredRatingSystems');
    const minimumAgeForAdultTVContent = parseInteger(getSettingValueByNameFromDeviceSettings(deviceSettings, 'parentalControl', 'minimumAgeForAdultTVContent'));
    if (parentalControlBaseUrl !== null) {
      const parentalControlCompletedBaseUrl = `${parentalControlBaseUrl}/v8/${realm}/`;
      fireAndForget(
        ParentalControlManager.initializeApi(parentalControlCompletedBaseUrl, authenticationToken, countryCode, minimumAgeForAdultTVContent),
        'ntgAPI.emitter.ParentalControlManager.initializeApi',
      );
    } else {
      // No parental control (adult content could still be locked, but in this case, it couldn't be unlocked)
      ParentalControlManager.disable(minimumAgeForAdultTVContent);
    }

    // Initialize Didomi
    const apiKey = getSettingValueByNameFromDeviceSettings(deviceSettings, 'didomi', 'apiKey');
    const webNoticeId = getSettingValueByNameFromDeviceSettings(deviceSettings, 'didomi', 'webNoticeId');
    if (apiKey !== null && webNoticeId !== null) {
      dispatch(updateDidomiConfiguration({ apiKey, webNoticeId }));
    }

    // Load gaming error messages
    const errorMessagesUrl = getSettingValueByNameFromDeviceSettings(deviceSettings, 'account.service.gamestream', 'errorMessagesUrl');
    if (errorMessagesUrl !== null) {
      fireAndForget(GameManager.loadErrorMessages(errorMessagesUrl), 'ntgAPI.emitter.GameManager.loadErrorMessages');
    }

    // Initialize profiles (if enabled)
    const isProfilesEnabled = parseBoolean(getSettingValueByNameFromDeviceSettings(deviceSettings, 'userprofiles', 'isEnabled'));
    const avatarIds = getSettingValueByNameFromDeviceSettings(deviceSettings, 'userprofiles', 'avatarIds');
    const defaultRestrictions = getDefaultRestrictions(deviceSettings, 'userProfiles.parentalControl.defaultRestrictions');
    const defaultKidsRestrictions = getDefaultRestrictions(deviceSettings, 'userProfiles.parentalControl.defaultKidsRestrictions');
    ProfilesManager.initialize(isProfilesEnabled, avatarIds, defaultRestrictions, defaultKidsRestrictions, dispatch, getState);

    // Check for VOD teasing (future rent date display)
    const hideVodPreviewDates = parseBoolean(getSettingValueByNameFromDeviceSettings(deviceSettings, 'vod', 'hidePreviewDates'));
    dispatch(updateHideVodPreviewDates(hideVodPreviewDates));

    // DMS settings cache directive (default to true)
    const isCacheEnabledRawValue = getSettingValueByNameFromDeviceSettings(deviceSettings, 'app', 'dmsSettingsCache');
    const isCacheEnabled = isCacheEnabledRawValue !== null ? parseBoolean(isCacheEnabledRawValue) : true;
    LocalStorageManager.save(StorageKeys.OptimisticDmsSettingsEnabled, isCacheEnabled);
    if (!isCacheEnabled) {
      LocalStorageManager.delete(StorageKeys.OptimisticDmsSettings);
    }
  };

  ntgApiInstance = new NetgemApi(connectedCallback, disconnectedCallback, deviceSettingsCallback, deviceChannelsCallback, associatedDevicesCallback, quotaPvrCallback, boAuthenticationCallback);
};

export const initialize: (store: Store) => void = (store) => {
  const { dispatch, getState } = store;
  createNetgemApiLibraryInstance(dispatch, getState);
};

type PossibleEmitterType =
  | typeof REDUX_MSG_REQUEST_CUSTOM_STRATEGY
  | typeof REDUX_MSG_REQUEST_DATA_COLLECTION
  | typeof REDUX_MSG_REQUEST_DATA_COLLECTION_COLLECTOR_ID
  | typeof REDUX_MSG_REQUEST_ENTITLEMENT_GET
  | typeof REDUX_MSG_REQUEST_ENTITLEMENT_RELEASE
  | typeof REDUX_MSG_REQUEST_GENERIC
  | typeof REDUX_MSG_REQUEST_GET_ASSOCIATED_DEVICES
  | typeof REDUX_MSG_REQUEST_GET_DEVICE_CHANNELS
  | typeof REDUX_MSG_REQUEST_GET_DEVICE_QUOTA_PVR
  | typeof REDUX_MSG_REQUEST_GET_DEVICE_SETTINGS
  | typeof REDUX_MSG_REQUEST_LIST_ALIAS_MULTI
  | typeof REDUX_MSG_REQUEST_LIST_ALIAS_POST
  | typeof REDUX_MSG_REQUEST_LOGIN
  | typeof REDUX_MSG_REQUEST_LOGOUT
  | typeof REDUX_MSG_REQUEST_NPVR_RECORDINGS_DELETE
  | typeof REDUX_MSG_REQUEST_NPVR_RECORDINGS_FUTURE
  | typeof REDUX_MSG_REQUEST_NPVR_RECORDINGS_LIST
  | typeof REDUX_MSG_REQUEST_NPVR_RECORDINGS_METADATA
  | typeof REDUX_MSG_REQUEST_NPVR_RECORDINGS_QUOTA
  | typeof REDUX_MSG_REQUEST_NPVR_RECORDINGS_RETRY
  | typeof REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_CREATE
  | typeof REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_DELETE
  | typeof REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_LIST
  | typeof REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_LIST_FULL
  | typeof REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_METADATA
  | typeof REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_UPDATE
  | typeof REDUX_MSG_REQUEST_PERSONAL_DATA_DELETE
  | typeof REDUX_MSG_REQUEST_PERSONAL_DATA_GET
  | typeof REDUX_MSG_REQUEST_PERSONAL_DATA_POST
  | typeof REDUX_MSG_REQUEST_SECTION_FEED
  | typeof REDUX_MSG_REQUEST_BO_AUTHENTICATE
  | typeof REDUX_MSG_REQUEST_BO_DISCOVERY
  | typeof REDUX_MSG_REQUEST_BO_FAVORITE_ADD
  | typeof REDUX_MSG_REQUEST_BO_FAVORITE_DELETE
  | typeof REDUX_MSG_REQUEST_BO_FAVORITE_LIST
  | typeof REDUX_MSG_REQUEST_BO_PERSONAL_DATA
  | typeof REDUX_MSG_REQUEST_BO_PURCHASE
  | typeof REDUX_MSG_REQUEST_BO_PURCHASE_LIST
  | typeof REDUX_MSG_REQUEST_BO_PURCHASE_STATUS
  | typeof REDUX_MSG_REQUEST_BO_STREAM_CREATE
  | typeof REDUX_MSG_REQUEST_BO_STREAM_START
  | typeof REDUX_MSG_REQUEST_BO_STREAM_STOP
  | typeof REDUX_MSG_REQUEST_BO_STREAMS_GET;

export type NetgemApiEmitterType = {
  emit: (type: PossibleEmitterType, payload: any) => Promise<any>,
};

type NtgApiActionPayloadType = {
  signal?: AbortSignal,
  uri: string,
};

export type ApplicationInfo = {
  appVersion: string,
  browserName: string,
  browserVersion: string,
  displayResolution: string,
  fcmProjectId: string,
  fcmToken: string,
  osVersion: string,
  osName: string,
  screenResolution: string,
  timestamp: string,
};

type NtgApiActionDmsSettingsPayloadType = {
  ...NtgApiActionPayloadType,
  appInfo: ApplicationInfo,
};

type NtgApiPayloadBaseType = {
  ...NtgApiActionPayloadType,
  authent: boolean,
  method: HttpMethod,
};

type NtgApiReducerPayloadType = {
  ...NtgApiPayloadBaseType,
  responseType?: string,
  state?: CombinedReducers,
};

type NtgApiPayloadWithETagType = {
  ...NtgApiPayloadBaseType,
  eTag: string,
};

type NtgLogoutRequestPayloadType = {
  logoutUrl?: string,
};

type NtgLoginRequestPayloadType = {
  applicationId: string,
  authDeviceUrl: string,
  deviceKey: string,
  isFirstToken: ?boolean,
  oem: ?string,
  signal?: AbortSignal,
  subscriberId: string,
  upgradeDeviceUrl: string,
  version: ?string,
};

type NtgEntitlementRequestPayloadType = {
  ...NtgApiPayloadBaseType,
  applicationId: string,
  channelName: string,
  customData: string,
  service: string,
  url: string,
};

type NtgListAliasPostRequestPayloadType = {
  ...NtgApiPayloadBaseType,
  bodyParam: string,
};

type NtgScheduledRecordingsCreatePayloadType = {
  ...NtgApiPayloadBaseType,
  channelId: ?string,
  endMargin: number,
  fromBeginning: boolean,
  fromUtc: ?string,
  increasingEpisodes: ?boolean,
  recordsToKeep: ?number,
  scheduledRecordKind: ScheduledRecordingsKindType,
  startMargin: number,
  target: string,
  toUtc: ?string,
};

type NtgScheduledRecordingsUpdatePayloadType = {
  ...NtgApiPayloadWithETagType,
  assetId: string,
  fromUtc: ?string,
  increasingEpisodes: ?boolean,
  recordsToKeep: ?number,
  toUtc: ?string,
};

type NtgPersonalDataPostRequestPayloadType = {
  ...NtgApiPayloadWithETagType,
  data: any,
};

type NtgSectionFeedRequestPayloadType = {
  searchString: ?string,
  section: NETGEM_API_V8_SECTION,
  signal?: AbortSignal,
  state: CombinedReducers,
};

type NtgDataCollectionPayloadType = {
  ...NtgApiPayloadBaseType,
  content: NETGEM_API_V8_DATA_COLLECTION_BATCH_PAYLOAD,
  headers: NETGEM_API_V8_REQUEST_HEADERS,
};

type BOApiPayloadBaseType = {
  authent: boolean,
  eTag?: string,
  headers: NETGEM_API_V8_REQUEST_HEADERS,
  method: HttpMethod,
  signal?: AbortSignal,
  uri: string,
};

type BOFavoritesApiPayloadType = {
  ...BOApiPayloadBaseType,
  titId?: string,
};

type BOWithVtiIdApiPayloadType = {
  ...BOApiPayloadBaseType,
  vtiId: number,
};

type BOPurchaseApiPayloadType = {
  ...BOWithVtiIdApiPayloadType,
  promocode: string,
};

type BOCreateStreamApiPayloadType = {
  ...BOApiPayloadBaseType,
  drm?: string,
  format?: string,
  license?: boolean,
  params?: BO_API_PARAMETERS_TYPE | null,
};

type BOStreamApiPayloadType = {
  ...BOApiPayloadBaseType,
  bookmark?: string,
  keepAlive?: boolean,
  streamId: number,
};

type BOAuthenticationSettingsRequestPayloadType = {
  appKey: string,
  applicationId: string,
  authenticationUrl: string,
  deviceKey: string,
  signal?: AbortSignal,
};

type BODiscoveryRequestPayloadType = {
  device: string,
  discoveryUrl: string,
  distributor: string,
  signal?: AbortSignal,
};

/* eslint-disable complexity, max-lines-per-function */
export const emit: (type: PossibleEmitterType, payload: any) => Promise<any> = (type, payload) => {
  if (!ntgApiInstance) {
    return Promise.reject(createCustomNetworkErrorFromKey('common.messages.errors.ntg_api_not_initialized'));
  }

  switch (type) {
    case REDUX_MSG_REQUEST_LOGIN: {
      const { applicationId, authDeviceUrl, deviceKey, isFirstToken, oem, signal, subscriberId, upgradeDeviceUrl, version } = payload as NtgLoginRequestPayloadType;
      return ntgApiInstance.sendLoginRequest(authDeviceUrl, upgradeDeviceUrl, applicationId, subscriberId, deviceKey, oem, version, isFirstToken, signal);
    }

    case REDUX_MSG_REQUEST_LOGOUT: {
      const { logoutUrl } = payload as NtgLogoutRequestPayloadType;
      return ntgApiInstance.sendLogoutRequest(logoutUrl);
    }

    case REDUX_MSG_REQUEST_GET_DEVICE_SETTINGS: {
      const { appInfo, signal } = payload as NtgApiActionDmsSettingsPayloadType;
      return ntgApiInstance.getDeviceSettings(appInfo, signal);
    }

    case REDUX_MSG_REQUEST_GET_DEVICE_CHANNELS: {
      const { signal } = payload as NtgApiActionPayloadType;
      return ntgApiInstance.getDeviceChannelList(signal);
    }

    case REDUX_MSG_REQUEST_GET_ASSOCIATED_DEVICES: {
      const { signal } = payload as NtgApiActionPayloadType;
      return ntgApiInstance.getAssociatedDeviceList(signal);
    }

    case REDUX_MSG_REQUEST_GET_DEVICE_QUOTA_PVR:
      return ntgApiInstance.getQuotaPvr();

    case REDUX_MSG_REQUEST_ENTITLEMENT_GET:
    case REDUX_MSG_REQUEST_ENTITLEMENT_RELEASE: {
      const { applicationId, authent, channelName, customData, method, service, signal, uri, url } = payload as NtgEntitlementRequestPayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendNtgEntitlementRequest(uri, applicationId, channelName, customData, service, url, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_LIST_ALIAS_MULTI:
    case REDUX_MSG_REQUEST_LIST_ALIAS_POST: {
      const { authent, bodyParam, method, signal, uri } = payload as NtgListAliasPostRequestPayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8ListAliasPostRequest(uri, bodyParam, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_SECTION_FEED: {
      const { searchString, section, signal, state } = payload as NtgSectionFeedRequestPayloadType;
      return ntgApiInstance.sendV8SectionFeedRequest(section, searchString, state, signal);
    }

    case REDUX_MSG_REQUEST_GENERIC: {
      const { authent, method, responseType, signal, state, uri } = payload as NtgApiReducerPayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8GenericRequest(uri, method, authenticationToken, responseType, state, signal);
    }

    case REDUX_MSG_REQUEST_CUSTOM_STRATEGY: {
      const { signal, uri } = payload as NtgApiActionPayloadType;
      return ntgApiInstance.sendV8CustomStrategyRequest(uri, signal);
    }

    case REDUX_MSG_REQUEST_PERSONAL_DATA_DELETE: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendPersonalDataDeleteRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_PERSONAL_DATA_GET: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendPersonalDataGetRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_PERSONAL_DATA_POST: {
      const { authent, data, eTag, method, signal, uri } = payload as NtgPersonalDataPostRequestPayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendPersonalDataPostRequest(uri, data, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_RECORDINGS_DELETE: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8RecordingsDeleteRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_RECORDINGS_FUTURE: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8RecordingsFutureRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_RECORDINGS_LIST: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8RecordingsListRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_RECORDINGS_METADATA: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8RecordingsMetadataRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_RECORDINGS_QUOTA: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8RecordingsQuotaRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_RECORDINGS_RETRY: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8RecordingsRetryRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_CREATE: {
      const { authent, channelId, endMargin, fromBeginning, fromUtc, increasingEpisodes, method, recordsToKeep, scheduledRecordKind, signal, startMargin, target, toUtc, uri } =
        payload as NtgScheduledRecordingsCreatePayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8ScheduledRecordingCreateRequest(
        uri,
        scheduledRecordKind,
        target,
        fromBeginning,
        startMargin,
        endMargin,
        channelId,
        fromUtc,
        toUtc,
        increasingEpisodes,
        recordsToKeep,
        method,
        authenticationToken,
        signal,
      );
    }

    case REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_DELETE: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8ScheduledRecordingDeleteRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_LIST: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8ScheduledRecordingListRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_LIST_FULL: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8ScheduledRecordingListRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_METADATA: {
      const { authent, eTag, method, signal, uri } = payload as NtgApiPayloadWithETagType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8ScheduledRecordingMetadataRequest(uri, eTag, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_NPVR_SCHEDULED_RECORDINGS_UPDATE: {
      const { assetId, authent, eTag, fromUtc, increasingEpisodes, method, recordsToKeep, signal, toUtc, uri } = payload as NtgScheduledRecordingsUpdatePayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendV8ScheduledRecordingUpdateRequest(uri, assetId, fromUtc, toUtc, eTag, increasingEpisodes, recordsToKeep, method, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_DATA_COLLECTION_COLLECTOR_ID: {
      const { signal, uri } = payload as NtgApiActionPayloadType;
      const { authenticationToken } = ntgApiInstance;
      return ntgApiInstance.sendV8DataCollectionCollectorIdRequest(uri, authenticationToken, signal);
    }

    case REDUX_MSG_REQUEST_DATA_COLLECTION: {
      const { content, headers, method, signal, uri } = payload as NtgDataCollectionPayloadType;
      return ntgApiInstance.sendV8DataCollectionRequest(uri, content, method, headers, signal);
    }

    case REDUX_MSG_REQUEST_BO_AUTHENTICATE: {
      const { appKey, applicationId, authenticationUrl, deviceKey, signal } = payload as BOAuthenticationSettingsRequestPayloadType;
      return ntgApiInstance.sendBOAuthenticationRequest(authenticationUrl, applicationId, deviceKey, appKey, signal);
    }

    case REDUX_MSG_REQUEST_BO_DISCOVERY: {
      const { device, discoveryUrl, distributor, signal } = payload as BODiscoveryRequestPayloadType;
      return ntgApiInstance.sendVideofuturDiscoveryRequest(discoveryUrl, device, distributor, signal);
    }

    case REDUX_MSG_REQUEST_BO_PERSONAL_DATA: {
      const { authent, headers, method, signal, uri } = payload as BOApiPayloadBaseType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendVideofuturRequest(uri, method, authenticationToken, null, headers, signal);
    }

    case REDUX_MSG_REQUEST_BO_STREAM_CREATE: {
      const { authent, headers, method, params, signal, uri } = payload as BOCreateStreamApiPayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendVideofuturRequest(uri, method, authenticationToken, params, headers, signal);
    }

    case REDUX_MSG_REQUEST_BO_STREAMS_GET: {
      const { authent, headers, method, signal, uri } = payload as BOApiPayloadBaseType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendVideofuturRequest(uri, method, authenticationToken, null, headers, signal);
    }

    case REDUX_MSG_REQUEST_BO_STREAM_START:
    case REDUX_MSG_REQUEST_BO_STREAM_STOP: {
      const { authent, bookmark, headers, keepAlive, method, signal, streamId, uri } = payload as BOStreamApiPayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendVideofuturRequest(
        uri,
        method,
        authenticationToken,
        // TODO: pass correct bookmark from player or undefined at app start
        { bookmark: bookmark ?? 'PT0S', vstId: streamId },
        headers,
        signal,
        keepAlive,
      );
    }

    case REDUX_MSG_REQUEST_BO_FAVORITE_LIST:
    case REDUX_MSG_REQUEST_BO_FAVORITE_ADD:
    case REDUX_MSG_REQUEST_BO_FAVORITE_DELETE: {
      const { authent, eTag, headers, method, signal, titId, uri } = payload as BOFavoritesApiPayloadType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendVideofuturRequest(uri, method, authenticationToken, titId ? { titId } : null, headers, signal, false, eTag);
    }

    case REDUX_MSG_REQUEST_BO_PURCHASE_STATUS:
    case REDUX_MSG_REQUEST_BO_PURCHASE: {
      const { authent, headers, method, promocode, signal, uri, vtiId } = payload as BOPurchaseApiPayloadType;
      const parameters: BO_API_PARAMETERS_TYPE = {
        promocode,
        vtiId,
      };
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendVideofuturRequest(uri, method, authenticationToken, parameters, headers, signal);
    }

    case REDUX_MSG_REQUEST_BO_PURCHASE_LIST: {
      const { authent, eTag, headers, method, signal, uri } = payload as BOApiPayloadBaseType;
      const authenticationToken = authent ? ntgApiInstance.authenticationToken : null;
      return ntgApiInstance.sendVideofuturRequest(uri, method, authenticationToken, null, headers, signal, false, eTag);
    }

    default:
      logError('No default case for emitter');
      return Promise.reject(
        new CustomNetworkError({
          message: 'No default case for emitter',
          status: -1,
        }),
      );
  }
};
/* eslint-enable complexity, max-lines-per-function */

export type RequestResponseMethodDefinitionType = (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType) => Promise<any>;

export type InternalActionDefinitionType = (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType) => void;
